import React from "react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogContentText,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export const useGracefulUpdate = (setOpenConfirmation) => {
  const [currentVersion, setCurrentVersion] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const location = useLocation();
  const lastCheckRef = useRef(0);
  const reminderTimeoutRef = useRef(null);
  const CHECK_COOLDOWN = 30000; // Don't check more than once every 30 seconds
  const REMINDER_DELAY = 120000; // Show reminder after 2 minutes

  const fetchVersion = async () => {
    // Skip if update is already detected
    if (updateAvailable) return;

    // Prevent too frequent checks (cooldown)
    const now = Date.now();
    if (now - lastCheckRef.current < CHECK_COOLDOWN) {
      return;
    }
    lastCheckRef.current = now;

    try {
      const res = await fetch(`/version.json?cache-bust=${Date.now()}`);
      const data = await res.json();

      // First time: just store the version
      if (!currentVersion) {
        setCurrentVersion(data.version);
        return;
      }

      // Version mismatch detected: show popup
      if (data.version !== currentVersion) {
        setUpdateAvailable(true);
        setOpenConfirmation(true);
      }
    } catch (err) {}
  };

  // Set up reminder timer when user clicks "Later"
  const scheduleReminder = () => {
    // Clear existing reminder
    if (reminderTimeoutRef.current) {
      clearTimeout(reminderTimeoutRef.current);
    }

    // Set new reminder
    reminderTimeoutRef.current = setTimeout(() => {
      setOpenConfirmation(true);
    }, REMINDER_DELAY);
  };

  useEffect(() => {
    // Check version on initial load
    fetchVersion();

    // Cleanup reminder on unmount
    return () => {
      if (reminderTimeoutRef.current) {
        clearTimeout(reminderTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Check version on route change (with cooldown protection)
    if (currentVersion) {
      fetchVersion();
    }
  }, [location.pathname]);

  useEffect(() => {
    // Check version when user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && currentVersion) {
        fetchVersion();
      }
    };

    // Check version when window regains focus
    const handleFocus = () => {
      if (currentVersion) {
        fetchVersion();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [currentVersion]);

  return { updateAvailable, scheduleReminder };
};

const UpdateVersion = ({
  // eslint-disable-next-line react/prop-types
  openConfirmation,
  // eslint-disable-next-line react/prop-types
  setOpenConfirmation,
  // eslint-disable-next-line react/prop-types
  scheduleReminder,
}) => {
  const handleReload = () => {
    window.location.reload(true);
  };

  const handleLater = () => {
    setOpenConfirmation(false);
    scheduleReminder(); // Schedule next reminder
  };

  return (
    <div>
      <Dialog
        open={openConfirmation}
        onClose={handleLater}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0px 10px 30px rgba(0,0,0,0.15)",
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleLater}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "grey.600",
            transition: "0.2s",
            "&:hover": { color: "grey.800" },
          }}
        >
          <CloseIcon size={22} />
        </IconButton>

        <Box
          sx={{
            p: 4,
            backgroundColor: "white",
            textAlign: "center",
            minWidth: { xs: 350, sm: 400 },
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold" color="#dc6200" mb={1}>
              Update Available!
            </Typography>

            <DialogContentText
              sx={{ color: "grey.600", fontSize: "1rem", lineHeight: 1.6 }}
            >
              A new version of the application is available. Please reload to
              get the latest features and improvements.
            </DialogContentText>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <Button
              onClick={handleLater}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "8px",
                borderColor: "grey.300",
                width: 112,
                height: 40,
                color: "grey.700",
                "&:hover": { backgroundColor: "grey.100" ,borderColor: "#dc6200"},
              }}
            >
              Later
            </Button>

            <Button
              onClick={handleReload}
              variant="contained"
              autoFocus
              sx={{
                textTransform: "none",
                fontWeight: "600",
                color: "white",
                borderRadius: "8px",
                width: 112,
                height: 40,
                backgroundColor: "#dc6200",
                boxShadow: "0px 3px 6px rgba(0,0,0,0.15)",
                "&:hover": { backgroundColor: "#f1740eff" },
                "&:active": { backgroundColor: "#f1740eff" },
              }}
            >
              Reload
            </Button>
          </Box>
        </Box>
      </Dialog>
    </div>
  );
};

export default UpdateVersion;
